import React, { useEffect, useState, useRef } from 'react';
import { ChevronRightIcon } from 'lucide-react';
import { ParallaxHero } from './ParallaxHero';
import { useScrollReveal } from '../hooks/useScrollReveal';
export function HeroSection() {
  const revealRef = useScrollReveal();
  const statsRef = useRef<HTMLDivElement>(null);
  const [typedText, setTypedText] = useState('');
  const [startStats, setStartStats] = useState(false);
  const fullText = 'Staviame PWA aplikácie poháňané multimodálnou AI.';
  const [stats, setStats] = useState({
    projects: 0,
    accuracy: 0,
    budget: 0,
    sprints: 0
  });
  // Typing effect
  useEffect(() => {
    let i = 0;
    const timer = setInterval(() => {
      setTypedText(fullText.slice(0, i));
      i++;
      if (i > fullText.length) {
        clearInterval(timer);
      }
    }, 40); // Speed of typing
    return () => clearInterval(timer);
  }, []);
  // Stats Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setStartStats(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.5
    });
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);
  // Stats Counter Logic
  useEffect(() => {
    if (!startStats) return;
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;
    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      const easeOutQuad = 1 - (1 - progress) * (1 - progress);
      setStats({
        projects: Math.floor(easeOutQuad * 20),
        accuracy: Math.floor(easeOutQuad * 97),
        budget: Math.floor(easeOutQuad * 500),
        sprints: Math.floor(easeOutQuad * 14)
      });
      if (currentStep >= steps) {
        clearInterval(timer);
      }
    }, interval);
    return () => clearInterval(timer);
  }, [startStats]);
  return <section id="overview" ref={revealRef} className="pt-36 pb-28 px-4 sm:px-6 lg:px-8 text-center max-w-7xl mx-auto overflow-hidden" data-id="element-143">
      <div className="max-w-4xl mx-auto relative z-10" data-id="element-144">
        <p className="reveal text-xs font-semibold tracking-[0.2em] text-apple-gray uppercase mb-8" data-id="element-145">
          LARSEN EVANS ENTERPRISE × GEMINI 2.0
        </p>

        <h1 className="reveal reveal-delay-1 text-5xl md:text-7xl lg:text-8xl font-extrabold tracking-tight leading-[1.05] text-apple-black mb-8 min-h-[120px] md:min-h-[160px]" data-id="element-146">
          {typedText}
          <span className="animate-pulse text-apple-blue" data-id="element-147">|</span>
        </h1>

        <p className="reveal reveal-delay-2 text-xl md:text-2xl font-light text-apple-gray mb-12 max-w-3xl mx-auto leading-relaxed" data-id="element-148">
          Naše štúdio premieňa Gemini Multimodal Embeddings 2 na produkty, ktoré
          vidia, počujú a rozumejú. Aplikácie novej generácie pre firmy, ktoré
          chcú viesť trh.
        </p>

        <div className="reveal reveal-delay-3 flex flex-col sm:flex-row items-center justify-center gap-6 mb-20" data-id="element-149">
          <a href="#kontakt" className="group flex items-center text-lg font-semibold text-apple-blue hover:text-apple-blueHover transition-all duration-300" data-id="element-150">
            Začať konzultáciu
            <ChevronRightIcon className="w-5 h-5 ml-1 group-hover:translate-x-1.5 transition-transform duration-300" data-id="element-151" />
          </a>
          <a href="#portfolio" className="group flex items-center text-lg font-semibold text-apple-blue hover:text-apple-blueHover transition-all duration-300" data-id="element-152">
            Pozrieť portfólio
          </a>
        </div>
      </div>

      {/* Interactive 3D Parallax Hero Image */}
      <div className="reveal-scale reveal-delay-4" data-id="element-153">
        <ParallaxHero data-id="element-154" />
      </div>

      {/* Animated Stat Counters */}
      <div ref={statsRef} className="reveal reveal-delay-4 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto border-t border-gray-100 pt-16" data-id="element-155">
        <div className="flex flex-col items-center" data-id="element-156">
          <span className="text-4xl md:text-6xl font-extrabold text-apple-black mb-2 tracking-tight" data-id="element-157">
            {stats.projects}+
          </span>
          <span className="text-xs font-semibold text-apple-gray uppercase tracking-[0.15em]" data-id="element-158">
            Projektov
          </span>
        </div>
        <div className="flex flex-col items-center" data-id="element-159">
          <span className="text-4xl md:text-6xl font-extrabold text-apple-black mb-2 tracking-tight" data-id="element-160">
            {stats.accuracy}%
          </span>
          <span className="text-xs font-semibold text-apple-gray uppercase tracking-[0.15em]" data-id="element-161">
            Presnosť AI
          </span>
        </div>
        <div className="flex flex-col items-center" data-id="element-162">
          <span className="text-4xl md:text-6xl font-extrabold text-apple-black mb-2 tracking-tight" data-id="element-163">
            ${stats.budget}K
          </span>
          <span className="text-xs font-semibold text-apple-gray uppercase tracking-[0.15em]" data-id="element-164">
            Budget
          </span>
        </div>
        <div className="flex flex-col items-center" data-id="element-165">
          <span className="text-4xl md:text-6xl font-extrabold text-apple-black mb-2 tracking-tight" data-id="element-166">
            {stats.sprints}
          </span>
          <span className="text-xs font-semibold text-apple-gray uppercase tracking-[0.15em]" data-id="element-167">
            Dňové Šprinty
          </span>
        </div>
      </div>
    </section>;
}